import { useState, useCallback, useMemo, useEffect } from 'react';
import styles from './Transitions.module.scss';
import { SllProgram, useGetProgramLazyQuery, useGetMultiCollectionsLazyQuery, NormalMultiCollectionFragment } from '@api/graphql-codegen';
import Spinner from 'src/components/spinner/Spinner';
import Header from 'src/components/header/Header';
import NavLayout from './navLayout/NavLayout';
import { resetGoogleTranslateWidget, sortByPosition } from '@src/utils/helpers';

import GradeItem from './gradeItem/GradeItem';
import { ProgramID, programMenuOptions, programOptions } from '@src/constants';
import { useHistory } from 'react-router-dom';
import { logger } from '../../utils/logger';
import FooterPage from '@src/components/footer-page/FooterPage';

function Transitions() { 
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const history = useHistory();

  const [getProgram, {data, loading}] = useGetProgramLazyQuery({variables: { input: { id: ProgramID.AUSTRANSITIONS }}, fetchPolicy: 'network-only'});
  const [getBatchCollections, {data: batchData}] = useGetMultiCollectionsLazyQuery({fetchPolicy: 'network-only'});

  useEffect(() => {
    resetGoogleTranslateWidget();
  }, [])

  useEffect(() => {
    getProgram();
  }, [getProgram])

  useEffect(() => {
    history.push(`/program/transitions?pid=${ProgramID.AUSTRANSITIONS}`);
  }, [history])

  const refresh = useCallback(async () => {
    try {
      setRefreshing(true)
      await getProgram({ fetchPolicy: 'network-only' })
    } catch(err) {
      logger.error(err)
    } finally {
      setRefreshing(false)
    }
  }, [getProgram])

  useEffect(() => {
    const rootElement  = document.getElementById("root")
    if (rootElement) {
      setTimeout(() => {
        rootElement.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100)
    }
  }, [])

  const handleSelect = useCallback((gradeId: string) => {
    if (selectedId === gradeId) {
      setSelected(false);
      setSelectedId(null);
    } else {
      setSelected(true);
      setSelectedId(gradeId);
      setTimeout(() => {
        const courseElement = document.getElementById(gradeId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }, [selectedId, setSelected, setSelectedId])

  const handleNoneSelect = (gradeId?: string) => {
    setSelected(false);
    setSelectedId(null);

    if (gradeId) {
      setTimeout(() => {
        const courseElement = document.getElementById(gradeId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }

  const handleSelectProgram = useCallback((program: string) => {
    if (program === programMenuOptions[0].label) {
      history.replace('/lessons/studentvoiceseries');
    } else if (program === programMenuOptions[1].label) {
      history.replace('/lessons/transitions');
    } else if (program === programMenuOptions[2].label) {
      history.replace('/lessons?tab=library');
    } else if (program === programMenuOptions[3].label) {
      history.replace('/lessons/counsellor');
    }
  }, [history]);

  const unitsViewStyle = useMemo(() => {
    const classes = [styles.unitsView];

    return classes.join(' ');
  }, [])

  const mainViewStyle = useMemo(() => {
    const classes = [styles.libraryView];

    return classes.join(' ');
  }, [])

  const multiCollections = useMemo(() => {
    if (!data?.getProgram?.mcs) return []
    return [...(data?.getProgram.mcs ?? [])].sort(sortByPosition)
  }, [data?.getProgram])

  // Batch fetch all multi-collections in a single request
  useEffect(() => {
    const ids = multiCollections.map(c => c?.id).filter(Boolean) as string[];
    if (ids.length > 0) {
      getBatchCollections({ variables: { input: { ids } } });
    }
  }, [multiCollections, getBatchCollections]);

  // Index batch results by ID for fast lookup
  const batchDataMap = useMemo(() => {
    const map = new Map<string, NormalMultiCollectionFragment>();
    for (const mc of batchData?.getMultiCollections ?? []) {
      if (mc?.id) map.set(mc.id, mc);
    }
    return map;
  }, [batchData]);

  const lessonListView = (
    <div className={unitsViewStyle}>
      {<div id={'mainScrollView'} style={{height: '100%'}}>
        <NavLayout programName={programOptions[1].label} program={data?.getProgram as SllProgram} handleSelectProgram={handleSelectProgram}/>
          <div className={styles.mainView}>
            {loading && !refreshing ? <div className={styles.spinner}><Spinner /></div> : <div className={mainViewStyle}>
              {multiCollections.map((item, index) => {
                return (
                  <div id={`grade_${index}`} key={index} style={{position: 'relative', marginBottom: '80px'}}>
                    <GradeItem gradeId={item?.id ?? ""}
                      gradeName={item?.name ?? ""} selected={selected} selectedId={selectedId}
                      handleNoneSelect={() => handleNoneSelect(item?.id)} handleSelected={handleSelect}
                      refresh={refresh}
                      multiCollectionData={batchDataMap.get(item?.id ?? '')}
                    />
                  </div>
                )
              }
              )}
            </div>}
          </div>
        </div>
      }
    </div>
  )

  return (
    <>
      <Header />
      <div className={styles.wrapper}>
        <div className={styles.page}>
          {lessonListView}
        </div>
        <FooterPage />
      </div>
    </>
  );
}

export default Transitions;
